import React, { BaseSyntheticEvent, FC, memo, useContext, useEffect, useRef } from 'react'

import { isMobile } from 'react-device-detect'
import { useFormContext } from 'react-hook-form'
import { useWizardStep } from 'react-wizard-primitive'

import { AppContext } from 'Const'
import useEvent from 'Hooks/useEvent'
import useToggle from 'Hooks/useToggle'
import { ReactComponent as PaymentStepIcon } from 'Icons/steps/payment.svg'
import { CartFulfillmentKinds, PaymentMethods } from 'Types'
import { scrollToTopOfElement } from 'Utils'

import { useSpreedlyPayment } from 'Components/Spreedly'
import { useForm as useSquareForm } from 'Components/Square/contexts/form'

import { StepContext } from '..'
import NextStepBtn from '../NextStepBtn'

import GiftCardsArea from './GiftCardsArea'
import useStripePayment from './PaymentForm/Stripe/useStripePayment'
import PaymentOptions from './PaymentOptions'
import { usePayment } from './Provider'
import Terms from './Terms'

interface Payment {
  payByEnteredCard: boolean
  paymentMethod: PaymentMethods
  sourceId: string
}

export interface FormState {
  spreedlyPayment?: Payment & {
    saveCard: boolean
  }
  squarePayment?: Payment & {
    retainOnSuccess: boolean
  }
  stripePayment?: Payment
}

interface Props {
  fulfillmentKind?: CartFulfillmentKinds
}

// Checkout Step #3
const PaymentArea: FC<Props> = () => {
  const { cart, shop } = useContext(AppContext)
  const { tokenizeCreditCard } = useSpreedlyPayment()
  const { cardTokenizeRef } = useSquareForm()
  const { createPaymentMethod } = useStripePayment()
  const { checkout, errorMessage, paymentStatus, gateway, tokenizing, setTokenizing } = usePayment()
  const { checkouting } = useContext(StepContext)
  const { getValues } = useFormContext()
  const stepContainerRef = useRef<HTMLDivElement>(null)
  const { isPaymentRequired, isGiftCardAvailable } = shop

  const { isActive } = useWizardStep()

  const [GCLinkIsClicked, , clickGCLink] = useToggle(false)

  useEffect(() => {
    if (isMobile && isActive) {
      scrollToTopOfElement(stepContainerRef)
    }
  }, [isActive])

  // GC - gift cards
  // display GC area if
  // * we don't have Square fields or
  // * there are applied GC or
  // * 'Apply a gift card' is clicked
  const GCBlockIsDisplayed = !isPaymentRequired || cart.hasAppliedGiftCards || GCLinkIsClicked

  const onSubmit = useEvent((evt?: BaseSyntheticEvent) => {
    const payByEnteredCard = getValues(`${gateway.field}.payByEnteredCard`)
    if (payByEnteredCard) {
      setTokenizing(true)

      if (gateway.isStripe) createPaymentMethod()
      else if (gateway.isSquare) void cardTokenizeRef.current(evt)
      else if (gateway.isSpreedly) tokenizeCreditCard()
    } else {
      void checkout(evt)
    }
  })

  return (
    <div ref={stepContainerRef} hidden={!isActive}>
      <div className="checkout-step">
        <h2 className="checkout-step__header / h4">
          <PaymentStepIcon />
          <span>Payment</span>
        </h2>
        <p className="mt-1 / mb-0 / w-100">All transactions are secure and encrypted.</p>
        {isGiftCardAvailable && !GCBlockIsDisplayed ? (
          <div className="checkout-step__link / d-flex / justify-content-start / align-items-center / my-2">
            <button type="button" onClick={clickGCLink}>
              Apply a gift card
            </button>
          </div>
        ) : null}

        {isGiftCardAvailable && GCBlockIsDisplayed ? <GiftCardsArea /> : null}

        {isPaymentRequired ? (
          errorMessage ? (
            <div className="checkout-form__errors / d-block">
              <p>{errorMessage}</p>
            </div>
          ) : (
            <PaymentOptions {...{ isActive }} />
          )
        ) : null}
      </div>
      <Terms {...{ isPaymentRequired }} />
      <NextStepBtn
        disabled={paymentStatus.isError}
        loading={paymentStatus.isLoading || checkouting || tokenizing}
        onSubmit={onSubmit}
      />
    </div>
  )
}

export default memo(PaymentArea)
